// THE CLIENT INBOX — every client message waiting on YWS, one row each, as the
// mock draws it.
//
// ⭐ PURE, AND THE PAGE PRINTS WHAT IT RETURNS. The waits, the model's verdicts
// and what people decided come in already read (lib/server/client-slack-
// inbox.ts); this decides which wait is in the inbox, in what order, under
// which tab, and every sentence about it. Nothing here reads.
//
// ⭐ ONE RULE FOR "IS IT WAITING", SHARED WITH RESPONSE TIMES: `decideWait`.
// Response Times' "Waiting now" tile links here, so the two must count the
// same waits; both ask this function, never their own copy of it.
//
//   1. THE ANSWERED SKILL DECIDES "NEEDS A REPLY", WHEN THE CLOCK STARTS AND
//      WHAT ANSWERED IT (waits v2: lib/client-slack-waits-v2.ts), while its
//      read is current; else THE RULES do. The wait arrives decided (`needs`,
//      `by`, `end`), the set-aside check's yes included (a turn the answered
//      skill set aside as finished that also hands YWS something new arrives
//      owed, with `secondLook` saying what). THE RESPOND SKILL (which replaced
//      the triage) reads the owed turns still open, for the kind, the need
//      line, escalation, emoji and quick replies: its verdict is used while it
//      was made on the turn's words as they stand (its input hash). It has no
//      "needs a reply" of its own.
//   2. A PERSON OVERRIDES BOTH, while their mark was made on the same words:
//      "no reply needed" sets it aside, "needs a reply" (pulled back from the
//      model's set-aside list) puts it back, and "answered elsewhere" takes an
//      open wait out of the inbox and out of the timings (nobody knows when the
//      answer came). "Archive" takes an open wait nobody answered and nobody now
//      will (a months-old message): out of the inbox and out
//      of "Waiting now", but STILL COUNTED in the timings, as the miss it is
//      (Response Times' `RtWait.archived`). A mark on words the client has since added to is not
//      used: the wait is undecided again, and back in the inbox. A pulled-back
//      turn is read by the respond skill at the next tick exactly as an owed
//      one; until then it has no verdict, so it sits under
//      "Needs a reply" with "What they need isn't written yet", never under
//      the set-aside read's stand-in `ack`.
//      ⚠️ A PULL-BACK REOPENS A CLOSE ONLY WHERE NOTHING IN SLACK HAS A TIME a
//      wait a reaction acknowledged (rule 3 reads only an unmarked wait) or the
//      model read as settled (rule 4, the same). NEVER ONE WITH AN `end`: the
//      mark is on the client's words, which a reply doesn't change, so a later
//      real answer could never close it again. A pulled-back wait with an end
//      stays answered, here and there.
//      A WAIT A YWS MESSAGE CLOSED IS REOPENED BY REJECTING THAT ANSWER
//      instead (lib/server/client-slack-rejected-answers.ts): the popup's
//      "Still needs a reply" there keeps the rejected message, not a mark.
//      Waits v2 never ends the wait on it (`decideTurn`), so the wait arrives
//      here with no `end` (`reopened`), waiting, in "Waiting now" and out of
//      the timings until an answer that isn't rejected ends it. No rule here
//      reads the rejection: the wait's `end` already says it all.
//   3. A QUICK ACKNOWLEDGEMENT, ACKNOWLEDGED (the reaction gate): a YWS
//      reaction on the turn closes it when the answered skill named that
//      reaction as its answer, OR the kind is "a quick acknowledgement does"
//      (`ack`: the respond skill's) and someone at YWS reacted with anything
//      but 👀 ("seen" answers nothing; waits v2 leaves it out of `acked` and
//      `reaction`) — the reply box's emoji are exactly this. It leaves the
//      inbox and the timings, like answered elsewhere: a reaction is not a
//      reply, and Slack doesn't say when it came. A written-reply wait with a
//      reaction the skill didn't name is still waiting: a 👀 answers nothing.
//   4. SETTLED: the answered skill read it as owed but settled with no answer
//      in Slack (moved to a call or a DM, superseded by the client's next turn,
//      or answered by the client themselves). Out of the inbox and the timings,
//      as a reaction is. It is listed with the model's set-aside waits, its
//      reason beside it, and a person can pull it back exactly as they pull
//      back a set-aside one: rule 2's "needs a reply", on the same words.
//   5. IN THE INBOX = still open (no YWS answer), needs a reply, not answered
//      elsewhere or archived, not acknowledged, not settled.
//
// ⚠️ NO AGE CUTOFF. "Waiting now" counts every open wait over the 90-day read.
// The inbox lists exactly those waits, oldest first after the escalations; a
// person archives an old one (rule 2), never the clock.
//
// ⭐ A WAIT'S LENGTH IS WORKING TIME: the chips, their tones and "answered in"
// are on YWS's working hours (lib/working-hours.ts, the one clock), as Response
// Times' numbers are. "3h ago" (when a message was sent, a set-aside one's age)
// is when something happened, on the wall clock.
//
// Every time here is Slack's, in SECONDS; a mark's `at` is ours, in ms.

import type { FooterNote } from "@/components/dashboards/dashboard-footer";
import { HOUR, brandNow, clockNotes, dur, slackAsOfLine, slackScopePills, waited, type RtAsOf, type RtBrand, type RtPill, type RtTriage } from "@/lib/client-slack-response-times";
import { workingDayLength, type WorkingHours } from "@/lib/working-hours";
import { emojiText } from "@/lib/slack-mrkdwn";
import { answersAsReaction } from "@/lib/client-slack-waits-v2";
import type { StatusChip } from "@/lib/client-slack-action-items";

export const INBOX_PATH = "/brands/inbox";

// ─── The rule ─────────────────────────────────────────────────────────────────

/** What a person decided about a wait in the inbox (`client_inbox_state.state`; `archived` is client_inbox_archived's row). */
export type InboxMark = "answered_elsewhere" | "no_reply" | "pulled_back" | "archived";
export const INBOX_MARKS: readonly InboxMark[] = ["answered_elsewhere", "no_reply", "pulled_back", "archived"];

/** A kept mark: what, by whom, when (ms), and on which words. */
export interface KeptMark {
  state: InboxMark;
  by: string;
  at: number;
  inputHash: string;
}

/** The model's kept read of a wait (the respond skill's; the triage's before it), as the inbox needs it. */
export interface InboxVerdict {
  inputHash: string;
  needsReply: boolean;
  kind: "reply" | "ack";
  need: string;
  escalation: boolean;
  emoji: string[];
  quick: string[];
}

/** A wait, as the rule needs it: is it open, its words' hash, and what decided it (waits v2, lib/client-slack-waits-v2.ts). */
export interface DecidableWait {
  end: number | null;
  inputHash: string;
  /** Needs a reply, by the answered skill's current read or the rules. */
  needs: boolean;
  /** Who decided `needs` (and `end`): the answered skill, or the rules. Absent: the rules. */
  by?: "model" | "rules";
  /** Someone at YWS reacted to a waiting message, with anything but 👀. */
  acked?: boolean;
  /** The answered skill named a YWS reaction as the answer (the reaction gate's other half). */
  reaction?: boolean;
  /** The emoji that reaction is (the named one, else YWS's on the turn), skin tone stripped, or null (waits v2 `ackEmoji`). */
  ackEmoji?: string | null;
  /** The answered skill read it as settled with no answer in Slack, or null. */
  settled?: SettledKind | null;
  /** The answered skill's sentence on it: the set-aside list's reason (respond never reads a turn it set aside). */
  note?: string | null;
  /**
   * The set-aside check's line, when it put back a wait the answered skill set
   * aside as finished: what YWS must now do. The wait arrives owed (`needs`);
   * this says why.
   */
  secondLook?: string | null;
  /** A person rejected an answer of it, and the rejection stands: its `end` is past that answer. */
  reopened?: boolean;
}

export type SettledKind = "moved_off_slack" | "superseded" | "client_self_answered";

export interface Decision {
  /** Needs a reply, after the model and any person. */
  needs: boolean;
  /** Who decided `needs`. */
  by: "model" | "rules" | "person";
  /** The model's current read, or null when it has none on these words. */
  verdict: InboxVerdict | null;
  /** The person's current mark, or null when there is none on these words. */
  mark: KeptMark | null;
  /** Marked answered elsewhere while still open: out of the inbox and the timings. */
  elsewhere: boolean;
  /** Archived by a person while still open and owed: out of the inbox and "Waiting now", still a miss in the timings. */
  archived: boolean;
  /** Closed by a YWS reaction through the gate (the skill named it, or kind `ack` and a reaction) while it is open: out of the inbox and the timings. */
  acknowledged: boolean;
  /** The reaction that acknowledged it (Slack's name, skin tone stripped), or null: the popup's "Acknowledged 👍". */
  ackEmoji: string | null;
  /** Settled with no answer in Slack, by the answered skill's read, or null: out of the inbox and the timings. */
  settled: SettledKind | null;
  /**
   * The answered skill's settled read of an open, owed wait, whatever a person
   * marked: what a pull-back pulled it back from. `settled` is this, unmarked.
   */
  settledRead: SettledKind | null;
  /**
   * A YWS reaction closes this open, owed wait through the gate, whatever a
   * person marked: what a pull-back from the Closed tab pulled it back from.
   * `acknowledged` is this, unmarked.
   */
  ackedRead: boolean;
  /** The answered skill's sentence on the wait, or null: a settled wait's reason. */
  note: string | null;
  /** The set-aside check's line when it put the wait back as owed, or null. */
  secondLook: string | null;
  /** A person rejected an answer of it, and the rejection stands. */
  reopened: boolean;
  /** In the inbox: open, needs a reply, not answered elsewhere, not archived, not acknowledged, not settled. */
  waiting: boolean;
}

/** The one rule for a wait: see the header. */
export function decideWait(w: DecidableWait, verdict: InboxVerdict | undefined, mark: KeptMark | undefined): Decision {
  const t = verdict !== undefined && verdict.inputHash === w.inputHash ? verdict : null;
  const m = mark !== undefined && mark.inputHash === w.inputHash ? mark : null;
  let needs = w.needs;
  let by: Decision["by"] = w.by ?? "rules";
  // The verdict the pages show. Owed, or pulled back by a person: the respond
  // skill's read, which "needs a reply" by construction (the answered skill or
  // the person decides that). Set aside by the answered skill: its reason, in
  // the verdict's shape, so the set-aside list and the popup's pull-back read
  // as before. Otherwise none.
  const v: InboxVerdict | null =
    w.needs || m?.state === "pulled_back"
    ? t && { ...t, needsReply: true }
    : by === "model"
      ? { inputHash: w.inputHash, needsReply: false, kind: "ack", need: w.note ?? "", escalation: false, emoji: [], quick: [] }
      : null;
  if (m?.state === "no_reply") {
    needs = false;
    by = "person";
  } else if (m?.state === "pulled_back") {
    needs = true;
    by = "person";
  }
  const elsewhere = m?.state === "answered_elsewhere" && w.end === null;
  const archived = m?.state === "archived" && w.end === null && needs;
  // The reaction gate, unmarked: a person's pull-back or no-reply
  // speaks for itself, and the rules have no `ack` kind and name no reaction.
  const gate = (w.reaction ?? false) || (v !== null && v.needsReply && v.kind === "ack" && (w.acked ?? false));
  const ackedRead = w.end === null && w.needs && gate;
  const acknowledged = !elsewhere && m === null && needs && ackedRead;
  const settledRead = w.end === null && w.needs ? (w.settled ?? null) : null;
  const settled = !elsewhere && !acknowledged && m === null && needs ? settledRead : null;
  return {
    needs,
    by,
    // Pulled back from a reaction's close: a person said a reaction wasn't
    // enough, so it is no quick acknowledgement now, whatever respond read.
    verdict: v !== null && m?.state === "pulled_back" && ackedRead ? { ...v, kind: "reply" } : v,
    mark: m,
    elsewhere,
    archived,
    acknowledged,
    ackEmoji: acknowledged ? (w.ackEmoji ?? null) : null,
    settled,
    settledRead,
    ackedRead,
    note: w.note ?? null,
    secondLook: w.secondLook ?? null,
    reopened: w.reopened ?? false,
    waiting: w.end === null && needs && !elsewhere && !archived && !acknowledged && settled === null,
  };
}

/** A wait a person archived: its tag. */
const ARCHIVED_TAG = "Archived";

/** A settled wait's tag, short. */
const SETTLED_TAG: Record<SettledKind, string> = {
  moved_off_slack: "Handled off Slack",
  superseded: "Withdrawn or replaced",
  client_self_answered: "Client answered it",
};

/**
 * The tag beside the popup's highlighted message: what was decided about the
 * wait, so it says "Waiting on us" only when it is. Null once someone at YWS
 * has answered it in Slack, as before: the popup's chip and its aside say so.
 * The highlight itself is the thread's, on every one.
 */
export function waitTag(w: { end: number | null; decision: Decision }): string | null {
  const d = w.decision;
  if (d.waiting) return "Waiting on us";
  if (w.end !== null) return null;
  if (d.elsewhere) return "Answered elsewhere";
  if (d.archived) return ARCHIVED_TAG;
  if (d.acknowledged) return d.ackEmoji === null ? "Acknowledged" : `Acknowledged ${emojiText(d.ackEmoji)}`;
  if (d.settled !== null) return SETTLED_TAG[d.settled];
  // Set aside by the model, marked by a person, or a thanks the rules pass over.
  return "No reply needed";
}

/** The tag on the message that answered or closed a wait, wherever the popup draws it. */
export const ANSWER_TAG = "Answered";

/**
 * The message the popup shows as the wait's answer: the YWS message that
 * answered it (the skill's, or the rule's), or, when a YWS reaction closed it,
 * the message that reaction sits on. Null for any other wait: still waiting,
 * set aside, settled, or marked answered elsewhere (nothing in Slack answered
 * those).
 */
export function answerOf(w: { end: number | null; answerTs?: string | null; ackTs?: string | null; decision: Decision }): string | null {
  if (w.end !== null) return w.answerTs ?? null;
  return w.decision.acknowledged ? (w.ackTs ?? null) : null;
}

// ─── The page ────────────────────────────────────────────────────────────────

/** The waiting list's three tabs, and Closed: the waits that closed in the 90 days. */
export type InboxTab = "all" | "reply" | "ack" | "followup" | "closed";
const TABS: readonly InboxTab[] = ["all", "reply", "ack", "followup", "closed"];
export const parseInboxTab = (v: string | undefined): InboxTab => ((TABS as readonly string[]).includes(v ?? "") ? (v as InboxTab) : "all");

export interface InboxReading {
  source?: "slack" | "fathom";
  pod: string | null;
  brand: string | null;
  tab: InboxTab;
  /**
   * The set-aside list's page, from 1, or absent: the list folded on its first
   * page. Present, it is open on that page.
   */
  aside?: number;
  /** The Closed tab's page, from 2, or absent: its first. Read only on that tab. */
  page?: number;
}

/** `?page=`: the Closed tab's page, from 2; the first, junk included, is absent (the address carries only what differs). */
export const parseClosedPage = (v: string | undefined): number | undefined => {
  const n = Number.parseInt(v ?? "", 10);
  return Number.isFinite(n) && n >= 2 ? n : undefined;
};

/** `?aside=`: a page from 1, or absent. Junk reads as the first page, open. */
export const parseAsidePage = (v: string | undefined): number | undefined => {
  if (v === undefined) return undefined;
  const n = Number.parseInt(v, 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
};

/** The page's address for a reading: only what differs from the default is in it. */
export function inboxQuery(r: InboxReading): Array<[string, string]> {
  const pairs: Array<[string, string]> = [];
  if (r.source) pairs.push(["source", r.source]);
  if (r.pod !== null) pairs.push(["pod", r.pod]);
  if (r.brand !== null) pairs.push(["brand", r.brand]);
  if (r.tab !== "all") pairs.push(["tab", r.tab]);
  if (r.aside !== undefined) pairs.push(["aside", String(r.aside)]);
  if (r.tab === "closed" && r.page !== undefined && r.page > 1) pairs.push(["page", String(r.page)]);
  return pairs;
}

/** Who wrote a message, as a row draws them. */
export interface InboxPerson {
  name: string;
  initials: string;
  avatarUrl: string | null;
  yws: boolean;
}

/** One wait in the reader's brands, read and decided (lib/server/client-slack-inbox.ts). */
export interface InboxWait {
  fathom?: import("./fathom-followups").FathomFollowup;
  id: string;
  brand: string;
  channelId: string;
  /** The channel's name, without `#`. */
  channel: string;
  /** The thread's key, and the waiting messages' ts, oldest first (lib/client-slack-waits.ts). */
  threadTs: string;
  anchors: string[];
  /** The waiting messages' words, hashed: what a verdict or a mark must have been made on. */
  inputHash: string;
  start: number;
  end: number | null;
  /** How many client messages are waiting. */
  count: number;
  /** The latest waiting message: who, and its words on one line. */
  latest: { person: InboxPerson; text: string };
  decision: Decision;
  /** The message that answered it, and the one a closing reaction sits on (waits v2; `answerOf` picks). */
  answerTs?: string | null;
  ackTs?: string | null;
  /** The segmenter's conversation it sits in, or null for a unit it hasn't placed: the popup folds the rest of it in. */
  conversationId?: number | null;
  /** Who at YWS answered it in Slack (the message `end` is), by name, or null: the Closed tab's "Answered by". */
  answeredBy?: string | null;
  /** The same person's Slack id, or null: whose YWS login answered it, where the roster knows their Slack. */
  answeredById?: string | null;
  /** When the answered skill's current read was made (ms), or null for the rule's: a timeless close's order on the Closed tab. */
  readAt?: number | null;
  /** The action items linked to this wait's thread, not archived, oldest first. */
  items?: InboxItem[];
  /** The answers a person rejected that still stand, oldest first: who wrote each (by name, or null), who rejected it (email) and when (ms). */
  rejected?: InboxRejection[];
}

/** An answer a person rejected, as the popup needs it. */
export interface InboxRejection {
  id: number;
  answerTs: string;
  who: string | null;
  by: string;
  at: number;
}

/** An action item linked to a wait's conversation: the list's Action item column, and the popup's cards. */
export interface InboxItem {
  id: string;
  title: string;
  status: StatusChip;
  /** The assignee's name, or null. */
  assignee: string | null;
}

export interface InboxRow {
  source?: "Slack" | "Fathom";
  id: string;
  href: string;
  /** "30h", and its tone: within the hour, within 4 hours, over. */
  wait: string;
  waitTone: "ok" | "late" | "over";
  waitTitle: string;
  brand: string;
  channel: string;
  escalation: boolean;
  person: InboxPerson;
  /** "· 3 messages", or null for one. */
  more: string | null;
  text: string;
  /** The ✦ line: the model's, or what the rules can say. */
  need: string;
  needBy: "model" | "rules" | "person";
  kind: "reply" | "ack" | "followup";
  /** The Action item column: the first linked item's status, and how many more; null for none ("—"). */
  item: { status: StatusChip; title: string; more: number } | null;
}

export interface InboxDoneRow {
  id: string;
  href: string;
  brand: string;
  /** "Marked no reply needed by Stefan at 3:04 PM", or "Replied in the thread by Stefan at 3:04 PM". */
  how: string;
  /** A mark can be undone; a reply or reaction sent to Slack can't be, from here. */
  undo: boolean;
}

/** A reply or reaction the app sent to Slack as someone, as the inbox needs it. */
export interface InboxSent {
  waitId: string;
  kind: "reply" | "react";
  emoji: string | null;
  broadcast: boolean;
  by: string;
  /** ms */
  at: number;
}

export interface InboxAsideRow {
  id: string;
  href: string;
  brand: string;
  who: string;
  text: string;
  /** The model's reason, and how long ago: "A thank-you. · 2d ago", or a settled wait's "Handled off Slack: … · 2d ago". */
  why: string;
}

/** The model's set-aside list, one page of it. */
export interface InboxAside {
  /** "The model says no reply needed · 1,234": every one in scope. */
  title: string;
  /** Open: the address asked for a page. */
  open: boolean;
  /** This page's rows only: the page never draws the rest. */
  rows: InboxAsideRow[];
  /** "51–100 of 1,234, newest first.", or null when one page holds them all. */
  range: string | null;
  /** The pager's links, or null at either end. */
  newer: { href: string; label: string } | null;
  older: { href: string; label: string } | null;
}

export interface InboxClosedRow {
  source?: "Slack" | "Fathom";
  id: string;
  href: string;
  /** "2h ago", when it closed; null when nobody knows (a reaction, a settled read): its title says so. */
  when: string | null;
  whenTitle: string;
  brand: string;
  channel: string;
  person: InboxPerson;
  /** "· 3 messages", or null for one. */
  more: string | null;
  text: string;
  /** How it closed, in the popup tag's words: "Answered by Stefan in 5h", "Acknowledged 👍", "Handled off Slack", "Answered elsewhere · marked by Stefan", "Archived by Stefan". */
  how: string;
}

/** The Closed tab's list, one page of it. */
export interface InboxClosed {
  rows: InboxClosedRow[];
  /** What it says with no rows. */
  empty: string | null;
  /** "51–100 of 1,234, newest close first.", or null when one page holds them all. */
  range: string | null;
  newer: { href: string; label: string } | null;
  older: { href: string; label: string } | null;
}

export interface InboxModel {
  pods: RtPill[];
  brands: RtPill[] | null;
  tabs: Array<{ key: InboxTab; label: string; href: string; count: number }>;
  /** The body's one empty state, or null. */
  empty: { title: string; act: string; settings: boolean } | null;
  rows: InboxRow[];
  /** What the list says with no rows. */
  rowsEmpty: string | null;
  done: InboxDoneRow[];
  aside: InboxAside;
  /** The Closed tab's list, on that tab only; null on the others, whose list is `rows`. */
  closed: InboxClosed | null;
  /** The list's order, beside the tabs: "Escalations, then longest wait", or the Closed tab's. */
  order: string;
  notes: FooterNote[];
  /** A picked archived brand's "As of Oct 3": its waits' ages are as of then. */
  asOf: { text: string; title: string } | null;
  /** Every waiting row in reach, whatever the scope, archived brands' left out: the nav badge's number (`inboxTotal`). */
  total: number;
}

/**
 * How many waits are in the inbox over every brand in reach: the nav badge,
 * and All's pill. An archived brand's are left out: nobody owes its clients
 * a reply now, though its own page still lists what was waiting at its archive.
 */
export function inboxTotal(brands: readonly Pick<RtBrand, "slug" | "archived">[], waits: readonly Pick<InboxWait, "brand" | "decision">[]): number {
  const archived = new Set(brands.filter((b) => b.archived).map((b) => b.slug));
  return waits.filter((w) => w.decision.waiting && !archived.has(w.brand)).length;
}

/** The model's set-aside list shows this many a page, newest first (it grows without end, and only recent ones matter). */
export const ASIDE_PAGE = 50;

/** The Closed tab shows this many a page, newest close first. */
export const CLOSED_PAGE = 50;

const TZ = "America/New_York";
const dayFmt = new Intl.DateTimeFormat("en-CA", { timeZone: TZ });
const timeFmt = new Intl.DateTimeFormat("en-US", { timeZone: TZ, hour: "numeric", minute: "2-digit" });
const whenFmt = new Intl.DateTimeFormat("en-US", { timeZone: TZ, month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const plural = (n: number, one: string, many = `${one}s`): string => `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
const listOf = (names: string[]): string =>
  names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/**
 * How long a wait has waited in working hours, and its tone: the mock's
 * `waitClass` (1 hour and 4 hours, now working ones). A message sent
 * Friday evening is still "ok" on Monday at 9:30am.
 */
export function waitChip(start: number, now: number, hours: WorkingHours): { text: string; tone: "ok" | "late" | "over"; title: string } {
  const s = waited({ start, end: null }, now, hours);
  return {
    text: dur(s, workingDayLength(hours)),
    tone: s <= HOUR ? "ok" : s <= 4 * HOUR ? "late" : "over",
    title: `Waiting since ${whenFmt.format(new Date(start * 1000))} · ${dur(s, workingDayLength(hours))} in working hours`,
  };
}

/** The words for a person's mark, in the past tense: "Marked answered elsewhere". */
export const MARK_WORDS: Record<InboxMark, string> = {
  answered_elsewhere: "Marked answered elsewhere",
  no_reply: "Marked no reply needed",
  pulled_back: "Pulled back into the inbox",
  archived: "Archived",
};

/** A rejected answer, in the past tense: "Reopened by Stefan, Oct 18, 3:04 PM: Froilan Reyes's reply wasn't the answer". */
export function rejectedWords(r: Pick<InboxRejection, "who" | "by" | "at">, nameOf: (email: string) => string): string {
  return `Reopened by ${nameOf(r.by)}, ${whenFmt.format(new Date(r.at))}: ${r.who === null ? "the reply" : `${r.who}'s reply`} wasn't the answer`;
}

/** What the app sent, in the past tense: "Replied in the thread", "Reacted 👍 in Slack". */
export function sentWords(x: Pick<InboxSent, "kind" | "emoji" | "broadcast">): string {
  if (x.kind === "react") return `Reacted ${x.emoji ? emojiText(x.emoji) : ""} in Slack`.replace("  ", " ");
  return x.broadcast ? "Replied in a thread and the channel" : "Replied in the thread";
}

/** Why a settled wait isn't waiting, in the popup. */
const SETTLED_WORDS: Record<SettledKind, string> = {
  moved_off_slack: "The model reads this as handled off Slack (on a call, by email or in a DM), so it isn't waiting any more.",
  superseded: "The model reads this as withdrawn, or replaced by the client's next message, so it isn't waiting any more.",
  client_self_answered: "The model reads this as answered by the client themselves, so it isn't waiting any more.",
};

/** A settled wait's reason, short: the set-aside list's and the popup's. */
const SETTLED_REASON: Record<SettledKind, string> = {
  moved_off_slack: "Handled off Slack",
  superseded: "Withdrawn, or replaced by a later message",
  client_self_answered: "The client answered it themselves",
};

/** The same, inside a sentence: "the model read it as handled off Slack". */
const SETTLED_AS: Record<SettledKind, string> = {
  moved_off_slack: "handled off Slack",
  superseded: "withdrawn, or replaced by a later message",
  client_self_answered: "answered by the client themselves",
};

/** "Handled off Slack: <the answered skill's sentence>", or the reason alone with no sentence. */
export function settledReason(kind: SettledKind, note: string | null): string {
  const n = (note ?? "").trim();
  return n === "" ? `${SETTLED_REASON[kind]}.` : `${SETTLED_REASON[kind]}: ${n}`;
}

/**
 * How the set-aside check's line is introduced, on the ✦ line and in the
 * popup: short, as the row's ✦ line is one line (the walkthrough found a
 * longer one cut the check's own words off). "Second Look" is the skill's
 * name on its page.
 */
export const SECOND_LOOK_WORDS = "Second look:";

/** The ✦ line for a wait in the inbox: the model's, or what the rules can say. */
export function needLine(d: Decision): string {
  if (d.verdict !== null) return d.verdict.need;
  // Pulled back, and not read by the respond skill yet: it reads it at the next tick.
  if (d.mark?.state === "pulled_back") {
    if (d.ackedRead) return "Pulled back from “acknowledged”: a reaction closed it. What they need isn't written yet.";
    return d.settledRead !== null
      ? `Pulled back from “no reply needed”: the model read it as ${SETTLED_AS[d.settledRead]}. What they need isn't written yet.`
      : "Pulled back from “no reply needed”: the model read it as needing nothing. What they need isn't written yet.";
  }
  // Reopened by a person who rejected the answer that closed it, and
  // not read by the respond skill since.
  if (d.reopened && d.waiting) return "Reopened: the reply that closed it wasn't the answer. What they need isn't written yet.";
  // Put back by the set-aside check: its line, until respond writes what they
  // need, whatever a person marked since (the walkthrough found a no-reply mark
  // falling through to "not read by the model yet", which was false).
  if (d.secondLook !== null) return `${SECOND_LOOK_WORDS} ${d.secondLook}`;
  // Owed by the answered skill's read; the respond skill writes what they need
  // at the next tick, while it still waits.
  if (d.by === "model") return d.waiting ? "The model reads it as owed a reply. What they need isn't written yet." : "The model read it as owed a reply.";
  return "Not read by the model yet. By simple rules, it asks for something.";
}

/**
 * How a wait closed, for the Closed tab, or null for one that didn't: still
 * waiting, or never owed (set aside by the model, marked no reply needed, a
 * thanks the rules pass over). Closed is owed and then answered in Slack (the
 * model's answer or the rule's), acknowledged by a YWS reaction, settled with
 * no answer in Slack, or marked answered elsewhere or archived by a person.
 * `at` is when, in seconds, where anyone knows: the answer's time, or the
 * mark's. A reaction or a settled read has none (Slack keeps no time for a
 * reaction), so it sorts by the read that decided it (`readAt`), else the
 * message the reaction sits on, else the client's message.
 */
export function closeOf(
  w: Pick<InboxWait, "start" | "end" | "decision" | "answeredBy" | "readAt" | "ackTs" | "fathom">,
  nameOf: (email: string) => string,
  hours: WorkingHours,
): { how: string; at: number | null; sortAt: number } | null {
  const d = w.decision;
  if (d.waiting) return null;
  if (w.fathom) return { how: w.fathom.mark === "read" ? "Follow-up reviewed" : "Follow-up dismissed", at: (w.fathom.markedAt ?? 0) / 1000 || null, sortAt: (w.fathom.markedAt ?? 0) / 1000 || w.start };
  if (w.end !== null) {
    if (!d.needs) return null;
    const took = dur(waited(w, w.end, hours), workingDayLength(hours));
    return { how: w.answeredBy ? `Answered by ${w.answeredBy} in ${took}` : `Answered in ${took}`, at: w.end, sortAt: w.end };
  }
  if (d.elsewhere && d.mark !== null) return { how: `Answered elsewhere · marked by ${nameOf(d.mark.by)}`, at: d.mark.at / 1000, sortAt: d.mark.at / 1000 };
  if (d.archived && d.mark !== null) return { how: `Archived by ${nameOf(d.mark.by)}`, at: d.mark.at / 1000, sortAt: d.mark.at / 1000 };
  if (!d.acknowledged && d.settled === null) return null;
  const readAt = w.readAt ?? null;
  return { how: waitTag(w) as string, at: null, sortAt: readAt !== null ? readAt / 1000 : w.ackTs ? Number(w.ackTs) : w.start };
}

export function clientInbox(input: {
  /** Now, in seconds. */
  now: number;
  /** YWS's working hours (lib/server/working-hours.ts): the clock a wait's length is on. */
  hours: WorkingHours;
  brands: readonly RtBrand[];
  waits: readonly InboxWait[];
  reading: InboxReading;
  href: (r: InboxReading) => string;
  /** A wait's popup, on the same reading. */
  waitHref: (id: string) => string;
  /** Names for the emails that marked a wait. */
  nameOf: (email: string) => string;
  triage: RtTriage;
  anyChannel: boolean;
  podLabels: ReadonlyMap<string, string>;
  settings: boolean;
  /** What the app sent to Slack: Done here today lists today's, where the wait is no longer waiting. */
  sent?: readonly InboxSent[];
}): InboxModel {
  const { now, brands, reading, href } = input;
  // A new scope or tab starts the set-aside list over, folded: its pages are this scope's.
  const go = (o: Partial<InboxReading>): string => href({ ...reading, aside: undefined, page: undefined, ...o });
  const nameOfBrand = new Map(brands.map((b) => [b.slug, b.name]));
  // A wait's age is on its brand's clock: an archived brand's stops at its moment (`brandNow`).
  const bySlug = new Map(brands.map((b) => [b.slug, b]));
  const at = (slug: string): number => brandNow(bySlug.get(slug), now);

  const waits = input.waits.filter((w) => !reading.source || (w.fathom ? "fathom" : "slack") === reading.source);
  const hasFathom = waits.some((w) => w.fathom);
  const waiting = waits.filter((w) => w.decision.waiting);
  const countOf = (slug: string): number => waiting.filter((w) => w.brand === slug).length;
  const { pods, brands: brandPills, podBrands } = slackScopePills({ brands, countOf, pod: reading.pod, brand: reading.brand, go: (o) => go(o) });
  const scoped = reading.brand === null ? podBrands : podBrands.filter((b) => b.slug === reading.brand);
  const picked = reading.brand === null ? null : (brands.find((b) => b.slug === reading.brand) ?? null);
  // ⚠️ AN ARCHIVED BRAND'S WAITS ARE LISTED ONLY ON ITS OWN PAGE: over a pod they
  // would sit in the to-do list for good, owed to a client nobody serves now.
  const listed = picked !== null ? scoped : scoped.filter((b) => !b.archived);
  const inScope = new Set(listed.map((b) => b.slug));
  const mine = waiting.filter((w) => inScope.has(w.brand));
  const kindOf = (w: InboxWait): "reply" | "ack" | "followup" => w.fathom ? "followup" : w.decision.verdict?.kind ?? "reply";
  // Closed in the 90 days, in scope, newest close first. Neither the nav badge nor "Waiting now" counts it.
  const closedAll = waits
    .filter((w) => inScope.has(w.brand))
    .flatMap((w) => {
      const c = closeOf(w, input.nameOf, input.hours);
      return c === null ? [] : [{ w, c }];
    })
    .sort((a, b) => b.c.sortAt - a.c.sortAt || (a.w.id < b.w.id ? -1 : 1));
  const order = reading.tab === "closed" ? "Newest close first" : "Escalations, then longest wait";

  const tabs = (
    [
      ["all", "All"],
      ["reply", "Needs a reply"],
      ["ack", "Quick reply"],
      ["followup", "Meeting follow-ups"],
      ["closed", "Closed"],
    ] as Array<[InboxTab, string]>
  ).filter(([key]) => key !== "followup" || hasFathom || reading.tab === "followup").map(([key, label]) => ({ key, label, href: go({ tab: key }), count: key === "all" ? mine.length : key === "closed" ? closedAll.length : mine.filter((w) => kindOf(w) === key).length }));

  // ── The footer: what the list leaves out, and who decided what's in it.
  const notes: FooterNote[] = [];
  const readable = scoped.filter((b) => b.word === "reading");
  const shelved = readable.filter((b) => !inScope.has(b.slug));
  const dark = scoped.filter((b) => b.word === "not-set" || b.word === "bot-missing");
  if (dark.length > 0 && readable.length > 0) {
    notes.push({
      text: `${plural(dark.length, "brand")} in this scope ${dark.length === 1 ? "has" : "have"} no Slack channel the YWS bot can read, so no Slack messages from ${dark.length === 1 ? "it" : "them"} show here: ${listOf(dark.map((b) => b.name))}.`,
      defect: true,
    });
  }
  const reading90 = scoped.flatMap((b) => b.stillReading.map((c) => `${c} (${b.name})`));
  if (reading90.length > 0 && readable.length > 0) {
    notes.push({ text: `Still reading the last 90 days of ${listOf(reading90)}, so ${reading90.length === 1 ? "it is" : "they are"} left out until the read is done.`, caution: true });
  }
  // Picked, its footer must not say the bot reads its channel (the clean sentence's words).
  if (picked?.archived && picked.word === "reading") {
    notes.push({ text: `${picked.name} is archived, so its Slack channels aren't read: this is what was waiting at its archive date.` });
  }
  if (shelved.length > 0 && shelved.length < readable.length) {
    const one = shelved.length === 1;
    notes.push({ text: `${listOf(shelved.map((b) => b.name))} ${one ? "is" : "are"} archived, so ${one ? "its" : "their"} messages aren't listed here. Pick ${one ? "it" : "one"} to see what was waiting at its archive date.` });
  }
  const byRules = mine.filter((w) => w.decision.by === "rules").length;
  if (byRules > 0) {
    const why = input.triage.exhausted
      ? input.triage.limitUsd === 0
        ? "The model is off (its daily budget is set to $0)"
        : "The model's daily budget is spent until midnight UTC"
      : "The model hasn't read them yet";
    notes.push({
      text: `${why}, so simple rules put ${byRules === mine.length ? `${byRules === 1 ? "this message" : `all ${plural(byRules, "message")}`}` : `${byRules.toLocaleString("en-US")} of these ${plural(mine.length, "message")}`} here: a short thanks or okay is left out, but a few that need nothing still show.`,
      caution: true,
    });
  }

  // The clock the chips are on, said once (X7).
  notes.push(...clockNotes(input.hours));

  const blank = { pods, brands: brandPills, tabs, rows: [], rowsEmpty: null, done: [], aside: { title: "", open: false, rows: [], range: null, newer: null, older: null }, closed: null, order, notes, asOf: picked?.archived && picked.asOf ? slackAsOfLine(picked.asOf, now) : null, total: inboxTotal(brands, input.waits) };
  if (!input.anyChannel && !hasFathom) {
    return { ...blank, notes: [], empty: { title: "The YWS bot's channels haven't been read yet.", act: "Client messages show here once the Slack reader has run.", settings: false } };
  }
  if (picked !== null && picked.word !== "reading" && picked.empty !== null && !waits.some((w) => w.fathom && w.brand === picked.slug)) {
    return { ...blank, notes: [], empty: { ...picked.empty, settings: input.settings && picked.word !== "still-reading" } };
  }
  if (readable.length === 0 && !hasFathom) {
    const scopeWords =
      reading.pod === null ? "None of the brands" : reading.pod === "none" ? "None of the brands nobody leads" : `None of the brands in ${input.podLabels.get(reading.pod) ?? "this pod"}`;
    return {
      ...blank,
      notes: [],
      empty: {
        title: `${scopeWords} has a Slack channel the YWS bot can read.`,
        act: "Ask an admin to link each brand's client channel on Settings → Brands, and to add the bot to it.",
        settings: input.settings,
      },
    };
  }
  if (shelved.length === readable.length && !hasFathom) {
    const one = shelved.length === 1;
    return {
      ...blank,
      notes: [],
      empty: {
        title: one ? "The only brand here with a Slack channel is archived." : "The only brands here with a Slack channel are archived.",
        act: `Pick ${one ? "it" : "one"} to see what was waiting at its archive date.`,
        settings: false,
      },
    };
  }

  // ── The list: escalations first, then the longest wait.
  // The Closed tab's list is its own (below): no waiting wait's kind is "closed", so this is empty there.
  const rows: InboxRow[] = mine
    .filter((w) => reading.tab === "all" || kindOf(w) === reading.tab)
    .slice()
    .sort((a, b) => Number(b.decision.verdict?.escalation ?? false) - Number(a.decision.verdict?.escalation ?? false) || a.start - b.start || (a.id < b.id ? -1 : 1))
    .map((w) => {
      const chip = w.fathom ? { text: `${dur(Math.max(0, at(w.brand) - w.start))}`, tone: "ok" as const, title: "Time since meeting; not a Slack reply SLA" } : waitChip(w.start, at(w.brand), input.hours);
      return {
        id: w.id,
        source: w.fathom ? "Fathom" : "Slack",
        href: input.waitHref(w.id),
        wait: chip.text,
        waitTone: chip.tone,
        waitTitle: chip.title,
        brand: nameOfBrand.get(w.brand) ?? w.brand,
        channel: w.fathom ? w.fathom.meetingTitle : `#${w.channel}`,
        escalation: w.decision.verdict?.escalation ?? false,
        person: w.latest.person,
        more: w.count > 1 ? `· ${w.count} messages` : null,
        text: w.latest.text || "(an attachment)",
        need: w.fathom ? "Meeting commitment · review the linked action item" : needLine(w.decision),
        needBy: w.fathom || w.decision.verdict !== null ? "model" : w.decision.mark?.state === "pulled_back" ? "person" : "rules",
        kind: kindOf(w),
        item: w.items && w.items.length > 0 ? { status: w.items[0].status, title: w.items[0].title, more: w.items.length - 1 } : null,
      };
    });
  const rowsEmpty =
    rows.length > 0 || reading.tab === "closed"
      ? null
      : mine.length > 0
        ? `Nothing ${reading.tab === "ack" ? "for a quick reply" : "needing a written reply"} here. The other tab has ${plural(mine.length, "message")}.`
        : `Nothing waiting${reading.brand !== null ? " for this brand" : ""}. 🎉`;

  // ── Done here today, with Undo: the marks people made since New York's midnight.
  const today = dayFmt.format(new Date(now * 1000));
  const marked = waits
    .filter((w) => inScope.has(w.brand))
    .filter((w) => {
      const m = w.decision.mark;
      // A pull-back is not "done": the wait is back in the list above.
      return m !== null && dayFmt.format(new Date(m.at)) === today && (m.state === "no_reply" || w.decision.elsewhere || w.decision.archived);
    })
    .map((w) => {
      const m = w.decision.mark as KeptMark;
      return { at: m.at, row: { id: w.id, href: input.waitHref(w.id), brand: nameOfBrand.get(w.brand) ?? w.brand, how: `${MARK_WORDS[m.state]} by ${input.nameOf(m.by)} at ${timeFmt.format(new Date(m.at))}`, undo: true } };
    });
  // ...and what was sent from here today, once it took the wait out of
  // the list: a reaction on a written-reply wait leaves it waiting, so it isn't
  // done. The latest send per wait; no Undo, since it is in Slack.
  const byId = new Map(waits.map((w) => [w.id, w]));
  const latestSent = new Map<string, InboxSent>();
  for (const x of input.sent ?? []) if (dayFmt.format(new Date(x.at)) === today && (latestSent.get(x.waitId)?.at ?? -1) <= x.at) latestSent.set(x.waitId, x);
  const sentDone = Array.from(latestSent.values()).flatMap((x) => {
    const w = byId.get(x.waitId);
    if (!w || !inScope.has(w.brand) || w.decision.waiting || marked.some((d) => d.row.id === w.id)) return [];
    return [{ at: x.at, row: { id: w.id, href: input.waitHref(w.id), brand: nameOfBrand.get(w.brand) ?? w.brand, how: `${sentWords(x)} by ${input.nameOf(x.by)} at ${timeFmt.format(new Date(x.at))}`, undo: false } }];
  });
  const done: InboxDoneRow[] = [...marked, ...sentDone].sort((a, b) => b.at - a.at).map((d) => d.row);

  // ── The model says no reply needed: open waits it set aside, and open waits
  // it read as settled without a reply in Slack, that nobody has marked; newest
  // first, a page at a time. Only the page's rows are drawn.
  const asideAll = waits
    .filter((w) => !w.fathom && inScope.has(w.brand) && w.end === null && ((w.decision.by === "model" && !w.decision.needs) || w.decision.settled !== null))
    .sort((a, b) => b.start - a.start || (a.id < b.id ? -1 : 1));
  const pages = Math.max(1, Math.ceil(asideAll.length / ASIDE_PAGE));
  const page = Math.min(reading.aside ?? 1, pages);
  const from = (page - 1) * ASIDE_PAGE;
  const shown = asideAll.slice(from, from + ASIDE_PAGE);
  const fmt = (n: number): string => n.toLocaleString("en-US");
  const aside: InboxAside = {
    title: `The model says no reply needed · ${fmt(asideAll.length)}`,
    open: reading.aside !== undefined,
    rows: shown.map((w) => ({
      id: w.id,
      href: input.waitHref(w.id),
      brand: nameOfBrand.get(w.brand) ?? w.brand,
      who: w.latest.person.name,
      text: w.latest.text || "(an attachment)",
      why: `${w.decision.settled !== null ? settledReason(w.decision.settled, w.decision.note) : (w.decision.verdict?.need ?? "")} · ${dur(Math.max(0, now - w.start))} ago`,
    })),
    range: pages > 1 ? `${fmt(from + 1)}–${fmt(from + shown.length)} of ${fmt(asideAll.length)}, newest first.` : null,
    newer: page > 1 ? { href: href({ ...reading, aside: page - 1 }), label: `Show the previous ${ASIDE_PAGE}` } : null,
    older: page < pages ? { href: href({ ...reading, aside: page + 1 }), label: `Show the next ${Math.min(ASIDE_PAGE, asideAll.length - from - ASIDE_PAGE)}` } : null,
  };

  // ── The Closed tab: a page at a time, with the set-aside list's pager.
  let closed: InboxClosed | null = null;
  if (reading.tab === "closed") {
    const cPages = Math.max(1, Math.ceil(closedAll.length / CLOSED_PAGE));
    const cPage = Math.min(reading.page ?? 1, cPages);
    const cFrom = (cPage - 1) * CLOSED_PAGE;
    const cShown = closedAll.slice(cFrom, cFrom + CLOSED_PAGE);
    closed = {
      rows: cShown.map(({ w, c }) => ({
        id: w.id,
        source: w.fathom ? "Fathom" : "Slack",
        href: input.waitHref(w.id),
        when: c.at === null ? null : `${dur(Math.max(0, now - c.at))} ago`,
        whenTitle:
          c.at !== null
            ? `${w.fathom || w.decision.elsewhere || w.decision.archived ? "Marked" : "Answered"} ${whenFmt.format(new Date(c.at * 1000))}`
            : w.decision.acknowledged
              ? "Slack keeps no time for a reaction"
              : "The model read it as settled; nobody recorded when",
        brand: nameOfBrand.get(w.brand) ?? w.brand,
        channel: w.fathom ? w.fathom.meetingTitle : `#${w.channel}`,
        person: w.latest.person,
        more: w.count > 1 ? `· ${w.count} messages` : null,
        text: w.latest.text || "(an attachment)",
        how: c.how,
      })),
      empty: closedAll.length > 0 ? null : `Nothing closed in the last 90 days${reading.brand !== null ? " for this brand" : ""}.`,
      range: cPages > 1 ? `${fmt(cFrom + 1)}–${fmt(cFrom + cShown.length)} of ${fmt(closedAll.length)}, newest close first.` : null,
      newer: cPage > 1 ? { href: href({ ...reading, page: cPage - 1 }), label: `Show the previous ${CLOSED_PAGE}` } : null,
      older: cPage < cPages ? { href: href({ ...reading, page: cPage + 1 }), label: `Show the next ${Math.min(CLOSED_PAGE, closedAll.length - cFrom - CLOSED_PAGE)}` } : null,
    };
  }

  return { ...blank, empty: null, rows, rowsEmpty, done, aside, closed };
}

// ─── The popup ───────────────────────────────────────────────────────────────

export interface InboxPopupModel {
  title: string;
  /** "waiting 30h" while it waits, "waited 2d" once answered, "9d ago" (neutral) when it is open but out of the inbox. */
  chip: { text: string; tone: "ok" | "late" | "over" | "done" | "aside"; title: string };
  /** An archived brand's "As of Oct 3", beside the chip: its wait is as of then. */
  asOf: { text: string; title: string } | null;
  escalation: boolean;
  /**
   * The aside's first heading and its line. No line (and no heading) on a wait
   * that closed with nothing the respond skill wrote, or one a person set aside
   * with no read at all: a stand-in there ("Not read by the model yet…") read
   * as if it were still owed.
   */
  needHead: string;
  need: string | null;
  needBy: "model" | "rules" | "person";
  /**
   * What a person can do here, and what was done. `reject_answer` is
   * "Still needs a reply" on a wait a YWS message closed: it rejects that
   * message (`rejects`) as the answer, not a mark.
   */
  actions: Array<InboxMark | "reject_answer">;
  /** The message `reject_answer` rejects: the answer that closed it (its ts), or null. */
  rejects: string | null;
  /** The actions' heading, and the pull-back's words: "Still needs a reply" on a wait that closed, "Needs a reply" on a set-aside one. */
  actionsHead: string;
  pullBack: string;
  /**
   * A person's mark, with Undo. `atTag`: a pull-back, drawn under the wait's
   * tag rather than among the actions, since it says why the wait is here, not
   * what to do.
   */
  marked: { how: string; undo: boolean; atTag: boolean } | null;
  /**
   * The latest answer a person rejected that still stands: what it says,
   * and the rejection Undo takes back. Drawn under the wait's tag, as a
   * pull-back is.
   */
  rejected: { id: number; how: string } | null;
  /** The model said it needs nothing: its reason, with "Needs a reply" to pull it back. */
  modelSaysNo: string | null;
  /**
   * What the reply box offers: the model's emoji to react with, less 👀 (a 👀
   * closes nothing, so pressing it would leave the wait waiting), and its quick
   * replies to fill the box. The react action takes only these, so a 👀 can't be
   * posted from here. None on a wait pulled back from a reaction's close:
   * a person said a reaction wasn't enough.
   */
  emoji: string[];
  quick: string[];
  /** Why there is nothing to do: answered in Slack already, or acknowledged with a reaction. */
  answered: string | null;
  /**
   * The set-aside check put this wait back as owed: its words, whatever
   * respond has written since, so the popup always says why a finished-looking
   * message is here. Null otherwise.
   */
  secondLook: string | null;
}

export function inboxPopup(
  w: InboxWait,
  input: {
    now: number;
    /** YWS's working hours: the clock "waited" and "waiting" are on (X7). */
    hours: WorkingHours;
    brandName: string;
    nameOf: (email: string) => string;
    /** The latest the app sent for this wait. */
    sent?: InboxSent | null;
    /** The wait's brand, when it is archived: "waiting" is as of its moment (`brandNow`). */
    brand?: { archived: boolean; asOf?: RtAsOf };
  },
): InboxPopupModel {
  const d = w.decision;
  const v = d.verdict;
  const chip = waitChip(w.start, brandNow(input.brand, input.now), input.hours);
  const asOf = input.brand?.archived && input.brand.asOf ? slackAsOfLine(input.brand.asOf, input.now) : null;
  const answered = w.end !== null;
  const mark = d.mark;
  const actions: InboxPopupModel["actions"] = [];
  // A pulled-back wait is in the inbox like any other, so it can be finished
  // like any other; the new mark replaces the pull-back. A settled wait is set
  // aside like the model's "no reply needed", and pulled back the same way. A
  // wait a YWS reaction closed is pulled back the same way: the mark, on the
  // same words, takes it past the reaction gate (rule 3 reads only an unmarked
  // wait). One answered in Slack can't be: rule 2 never reopens a wait with an
  // end (the header says why). Its answer is rejected instead, when a YWS
  // message closed a wait that was owed.
  const rejects = answered && d.needs ? (w.answerTs ?? null) : null;
  if (rejects !== null) actions.push("reject_answer");
  else if (!answered && d.acknowledged && mark === null) actions.push("pulled_back");
  else if (!answered && !d.acknowledged && (mark === null || mark.state === "pulled_back")) {
    if (d.settled !== null) actions.push("pulled_back");
    else if (d.needs) actions.push("answered_elsewhere", "no_reply", "archived");
    else if (d.by === "model") actions.push("pulled_back");
  }
  // Settled, unmarked: the model's read is its reason, and nothing it wrote to send applies.
  const settledWhy = d.settled !== null ? settledReason(d.settled, d.note) : null;
  const latest = (w.rejected ?? []).at(-1) ?? null;
  const modelSaysNo = settledWhy ?? (v !== null && !v.needsReply && mark === null && !answered ? v.need : null);
  // What they need. A wait that isn't waiting shows a model's read or nothing:
  // respond's line, the answered skill's (a set-aside's or a settled one's
  // reason, as the set-aside list has it), or, set aside by a person and not
  // closed, the check's. Never a stand-in, which read as if it were still owed.
  // One waiting always says something.
  const closed = answered || d.acknowledged || d.elsewhere || d.archived || d.settled !== null;
  const needShown = d.waiting || v !== null || settledWhy !== null || (!closed && d.secondLook !== null);
  return {
    title: input.brandName,
    chip: answered
      ? { text: `waited ${dur(waited(w, input.now, input.hours), workingDayLength(input.hours))}`, tone: "done", title: `Sent ${whenFmt.format(new Date(w.start * 1000))} · counted in working hours` }
      : d.waiting
        ? { text: `waiting ${chip.text}`, tone: chip.tone, title: chip.title }
        : // Open but not waiting (set aside, settled, marked, acknowledged): its age, in no alarm colour.
          // When it was sent, not a wait: the wall clock.
          { text: `${dur(Math.max(0, input.now - w.start))} ago`, tone: "aside", title: `Sent ${whenFmt.format(new Date(w.start * 1000))}` },
    asOf: d.waiting ? asOf : null,
    escalation: settledWhy === null && (v?.escalation ?? false),
    needHead: settledWhy !== null ? "The model's read" : v !== null && v.needsReply && v.kind === "ack" ? "Just acknowledge" : v !== null && !v.needsReply ? "The model's read" : "What they need",
    need: !needShown ? null : (settledWhy ?? (v !== null && !v.needsReply && mark?.state !== "pulled_back" ? v.need : needLine(d))),
    needBy: settledWhy !== null || v !== null ? "model" : mark?.state === "pulled_back" ? "person" : "rules",
    actions,
    actionsHead: d.acknowledged || rejects !== null ? "Closed by mistake?" : modelSaysNo !== null ? "The model got it wrong?" : "Not something to answer here?",
    pullBack: d.acknowledged || d.settled !== null || rejects !== null ? "Still needs a reply" : "Needs a reply",
    rejects,
    marked: mark === null ? null : { how: `${MARK_WORDS[mark.state]} by ${input.nameOf(mark.by)}, ${whenFmt.format(new Date(mark.at))}`, undo: true, atTag: mark.state === "pulled_back" },
    rejected: latest === null ? null : { id: latest.id, how: rejectedWords(latest, input.nameOf) },
    modelSaysNo,
    // Pulled back from a reaction's close: another reaction can't close it (rule 3 reads only an unmarked wait), so the box offers none.
    emoji: settledWhy !== null || (mark?.state === "pulled_back" && d.ackedRead) ? [] : (v?.emoji ?? []).filter(answersAsReaction),
    quick: settledWhy !== null ? [] : (v?.quick ?? []),
    answered:
      (answered || d.acknowledged) && input.sent
        ? `${sentWords(input.sent)} by ${input.nameOf(input.sent.by)}, ${whenFmt.format(new Date(input.sent.at))}, from here. It isn't waiting any more.`
        : answered
      ? "Someone at YWS has answered this in Slack, so it isn't waiting any more."
      : d.acknowledged
        ? "Someone at YWS reacted to this in Slack, and a quick acknowledgement is all it needed, so it isn't waiting any more."
        : d.settled !== null
          ? SETTLED_WORDS[d.settled]
          : null,
    secondLook: d.secondLook === null || mark?.state === "no_reply" ? null : `${SECOND_LOOK_WORDS} ${d.secondLook}`,
  };
}

